import introJs from 'intro.js'
import 'intro.js/introjs.css'
import '@/scss/onBoarding.scss'
import onboardingApiService from './onboardingApiService'

let intro = null
export const STORAGE_KEY = 'onboarding_tours'
const USER_CREATED_AT_KEY = 'onboarding_user_created_at'

// Estado de sesión (en memoria, se resetea en cada login)
let _token = null
let _synced = false
let _syncPromise = null

// Cola de tours
let _queue = []
let _running = false

const defaultOptions = {
  showProgress: false,
  showBullets: false,
  exitOnOverlayClick: false,
  nextLabel: 'Siguiente >',
  prevLabel: '< Atrás',
  doneLabel: 'Finalizar',
  showStepNumbers: true,
  stepNumbersOfLabel: 'de',
  autoposition: true
}

/* -------------------------
  LocalStorage helpers
-------------------------- */

const getUserCreatedAt = () => localStorage.getItem(USER_CREATED_AT_KEY) || null
const saveUserCreatedAt = (date) => {
  if (date) localStorage.setItem(USER_CREATED_AT_KEY, date)
}

const getToursStorage = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}
  } catch (e) {
    console.error('Error leyendo onboarding storage:', e)
    return {}
  }
}

const saveToursStorage = (tours) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tours))
}

const isTourCompleted = (tourName) => {
  return getToursStorage()[tourName]?.completed === true
}

const markTourCompleted = (tourName) => {
  const tours = getToursStorage()
  tours[tourName] = { completed: true, lastStep: null }
  saveToursStorage(tours)

  if (_token) {
    onboardingApiService.upsertTour(_token, { tourName, completed: true, lastStep: 0 })
  }
}

const saveTourProgress = (tourName, step) => {
  const tours = getToursStorage()
  const current = tours[tourName]

  if (current?.completed === true) return

  const prevStep = current?.lastStep || 0
  tours[tourName] = {
    ...(current || {}),
    completed: false,
    lastStep: Math.max(prevStep, step)
  }
  saveToursStorage(tours)
}

// Solo se llama al cerrar el tour (X), sincroniza el progreso actual al backend
const flushProgressToBackend = (tourName) => {
  if (!_token) return
  const tour = getToursStorage()[tourName]
  if (!tour || tour.completed) return
  onboardingApiService.upsertTour(_token, {
    tourName,
    completed: false,
    lastStep: tour.lastStep
  })
}

const getTourProgress = (tourName) => {
  return getToursStorage()[tourName]?.lastStep || null
}

/* -------------------------
  Cola de tours
-------------------------- */

const runTour = (config) => {
  return new Promise(async (resolve) => {
    const { tourName, steps, breakTour, breakStep, startAt, onCloseCallback, options } = config

    intro = introJs()
    intro.setOptions({ ...defaultOptions, ...(options || {}), steps })

    const savedStep = getTourProgress(tourName)

    // resolve() puede ser llamado por onComplete y luego por onExit (intro.js los dispara ambos)
    let settled = false
    const done = () => { if (!settled) { settled = true; resolve() } }

    intro.onBeforeChange(() => {
      if (breakTour && breakStep != null && breakStep === intro.getCurrentStep()) {
        intro.exit()
      }
    })

    intro.onChange(() => {
      const step = intro.getCurrentStep()
      if (step != null && step >= 0) saveTourProgress(tourName, step + 1)
    })

    intro.onComplete(() => {
      markTourCompleted(tourName)
      done()
    })

    intro.onExit(() => {
      const step = intro.getCurrentStep()
      if (step != null && step >= 0) saveTourProgress(tourName, step + 1)
      flushProgressToBackend(tourName)
      if (onCloseCallback && typeof onCloseCallback === 'function') onCloseCallback()
      done()
    })

    const resumeStep = savedStep > startAt ? savedStep : startAt
    if (breakTour && resumeStep > breakStep) { resolve(); return }

    await intro.start()
    if (resumeStep != null) intro.goToStepNumber(resumeStep)
  })
}

const processQueue = async () => {
  if (_running || _queue.length === 0) return
  _running = true
  try {
    while (_queue.length > 0) {
      const config = _queue.shift()
      await runTour(config)
    }
  } finally {
    _running = false
  }
}

/* -------------------------
  Sync backend → localStorage
  Solo una vez por sesión (mientras _token sea válido)
-------------------------- */

const syncBackendToStorage = async () => {
  if (!_token) return
  if (_synced) return

  // Si ya hay una sincronización en curso, esperarla
  if (_syncPromise) {
    await _syncPromise
    return
  }

  _syncPromise = (async () => {
    const result = await onboardingApiService.getTours(_token)
    if (!result) return

    const { tours: remoteTours, userCreatedAt } = result
    saveUserCreatedAt(userCreatedAt)

    const localTours = getToursStorage()

    Object.entries(remoteTours).forEach(([tourName, remote]) => {
      const local = localTours[tourName]
      // Backend gana en estado "completed"; se toma el mayor paso avanzado
      localTours[tourName] = {
        completed: remote.completed || local?.completed || false,
        lastStep: Math.max(remote.lastStep || 0, local?.lastStep || 0)
      }
    })

    saveToursStorage(localTours)
    _synced = true
  })()

  await _syncPromise
  _syncPromise = null
}

/*
  tourName  - Nombre único del tour para guardar en localstorage (obligatorio)
  steps     - Array de pasos para el tour
  breakTour - si se desea interrumpir el tour
  breakStep - en qué paso interrumpir (0-based)
  startAt   - en qué paso debe iniciar cuando se retoma (1-based)
*/

export default {
  setToken(token) {
    _token = token
    _synced = false
    _syncPromise = null
  },

  async syncFromBackend() {
    await syncBackendToStorage()
  },

  start({
    tourName,
    steps = [],
    releaseDate = null,
    showTo = null,
    breakTour = false,
    breakStep = null,
    startAt = null,
    onCloseCallback = null,
    options = {}
  }) {
    if (!steps.length) return
    if (isTourCompleted(tourName)) return
    if (releaseDate && showTo && !this.shouldShowTour(releaseDate, showTo)) return

    _queue.push({ tourName, steps, breakTour, breakStep, startAt, onCloseCallback, options })
    processQueue()
  },

  next() {
    intro?.nextStep()
  },

  prev() {
    intro?.previousStep()
  },

  exit() {
    intro?.exit()
  },

  destroy() {
    _queue = []
    _running = false
    intro?.exit()
    intro = null
  },

  /*
    Determina si un tour debe mostrarse según la fecha de creación del usuario y la fecha de lanzamiento del tour.

    showTo: 'new'      → solo usuarios creados DESPUÉS de releaseDate (usuarios nuevos)
    showTo: 'existing' → solo usuarios creados ANTES de releaseDate (usuarios antiguos)

    Si no hay fecha de usuario o de tour, se muestra por defecto.
  */
  shouldShowTour(releaseDate, showTo = 'new') {
    const userCreatedAt = getUserCreatedAt()
    if (!userCreatedAt || !releaseDate) return true
    const userDate = new Date(userCreatedAt)
    const tourDate = new Date(releaseDate)
    if (showTo === 'new') return userDate >= tourDate
    if (showTo === 'existing') return userDate < tourDate
    return true
  },

  // 🧹 Utilidad para QA o para reiniciar tours manualmente
  reset(tourName) {
    const tours = getToursStorage()

    if (tourName) {
      delete tours[tourName]
    } else {
      localStorage.removeItem(STORAGE_KEY)
      return
    }

    saveToursStorage(tours)
  },

  getStateTour() {
    return intro?.isActive()
  }
}
